import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ENABLE_TEST_ENVIRONMENT, IS_PRODUCTION } from '../config';

const TestEnvironment = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    daysFromNow: 7
  });
  const [signupForm, setSignupForm] = useState({
    eventId: '',
    confirmed: 10,
    tentative: 8,
    late: 5,
    declined: 7
  });

  useEffect(() => {
    loadStatus();
  }, []);

  // Disable test environment in production
  if (IS_PRODUCTION && !ENABLE_TEST_ENVIRONMENT) {
    return (
      <div className="card">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-8 h-8 bg-yellow-100 dark:bg-yellow-900/20 rounded-full flex items-center justify-center">
            <span className="text-yellow-600 dark:text-yellow-400 text-lg">⚠️</span>
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Test Environment</h2>
        </div>
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <p className="text-yellow-800 dark:text-yellow-200">
            Test environment features are disabled in production for security reasons. 
            This feature is only available in development mode or when explicitly enabled.
          </p>
        </div>
      </div>
    );
  }

  const loadStatus = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/admin/test-environment/status');
      setStatus(response.data);
    } catch (error) {
      console.error('Error loading test environment status:', error);
      setMessage('Error loading status');
    } finally {
      setLoading(false);
    }
  };

  const createTestUsers = async () => {
    try {
      setLoading(true);
      setMessage('');
      const response = await axios.post('/api/admin/test-environment/create-users');
      setMessage(response.data.message);
      await loadStatus();
    } catch (error) {
      console.error('Error creating test users:', error);
      setMessage('Error creating test users');
    } finally {
      setLoading(false);
    }
  };

  const createTestEvent = async () => {
    try {
      setLoading(true);
      setMessage('');
      const response = await axios.post('/api/admin/test-environment/create-event', eventForm);
      setMessage(response.data.message);
      setEventForm({ title: '', description: '', daysFromNow: 7 });
      await loadStatus();
    } catch (error) {
      console.error('Error creating test event:', error);
      setMessage('Error creating test event');
    } finally {
      setLoading(false);
    }
  };

  const signupUsersToEvent = async () => {
    try {
      setLoading(true);
      setMessage('');
      const response = await axios.post('/api/admin/test-environment/signup-users', {
        eventId: signupForm.eventId,
        signupConfig: {
          confirmed: parseInt(signupForm.confirmed),
          tentative: parseInt(signupForm.tentative),
          late: parseInt(signupForm.late),
          declined: parseInt(signupForm.declined)
        }
      });
      setMessage(response.data.message);
      setSignupForm({ eventId: '', confirmed: 10, tentative: 8, late: 5, declined: 7 });
      await loadStatus();
    } catch (error) {
      console.error('Error signing up users:', error);
      setMessage('Error signing up users');
    } finally {
      setLoading(false);
    }
  };

  const clearTestData = async () => {
    if (!window.confirm('Are you sure you want to clear all test data? This cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      setMessage('');
      const response = await axios.delete('/api/admin/test-environment/clear-data');
      setMessage(response.data.message);
      await loadStatus();
    } catch (error) {
      console.error('Error clearing test data:', error);
      setMessage('Error clearing test data');
    } finally {
      setLoading(false);
    }
  };

  const addPreferencesToExistingUsers = async () => {
    try {
      setLoading(true);
      setMessage('');
      const response = await axios.post('/api/admin/test-environment/add-preferences');
      setMessage(response.data.message);
      await loadStatus();
    } catch (error) {
      console.error('Error adding preferences:', error);
      setMessage('Error adding preferences');
    } finally {
      setLoading(false);
    }
  };

  const regeneratePreferences = async () => {
    if (!window.confirm('Are you sure you want to regenerate all preferences? This will clear existing preferences and create new ones.')) {
      return;
    }

    try {
      setLoading(true);
      setMessage('');
      const response = await axios.post('/api/admin/test-environment/regenerate-preferences');
      setMessage(response.data.message);
      await loadStatus();
    } catch (error) {
      console.error('Error regenerating preferences:', error);
      setMessage('Error regenerating preferences');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
        🧪 Test Environment Management
      </h2>

      {/* Status Display */}
      {status && (
        <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <h3 className="text-lg font-semibold mb-2 text-blue-900 dark:text-blue-100">
            Current Status
          </h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium">Test Users:</span> {status.testUsers}/30
            </div>
            <div>
              <span className="font-medium">Total Events:</span> {status.totalEvents}
            </div>
            <div>
              <span className="font-medium">Player Preferences:</span> {status.usersWithPlayerPrefs || 0}
            </div>
            <div>
              <span className="font-medium">Storyteller Preferences:</span> {status.usersWithStorytellerPrefs || 0}
            </div>
          </div>
          {status.events.length > 0 && (
            <div className="mt-3">
              <span className="font-medium">Events:</span>
              <ul className="mt-1 space-y-1">
                {status.events.map(event => (
                  <li key={event.id} className="text-sm">
                    {event.title} (ID: {event.id})
                  </li>
                ))}
              </ul>
            </div>
          )}
          {status.sampleUsers && status.sampleUsers.length > 0 && (
            <div className="mt-3">
              <span className="font-medium">Sample User Preferences:</span>
              <ul className="mt-1 space-y-1">
                {status.sampleUsers.map(user => (
                  <li key={user.id} className="text-sm">
                    <span className="font-medium">{user.username}:</span>
                    {user.preferences.preferred_players && (
                      <span className="ml-2 text-xs">
                        Players: {JSON.parse(user.preferences.preferred_players).join(', ')}
                      </span>
                    )}
                    {user.preferences.preferred_storyteller && (
                      <span className="ml-2 text-xs">
                        Storyteller: User {user.preferences.preferred_storyteller}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Message Display */}
      {message && (
        <div className="mb-4 p-3 bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200 rounded-lg">
          {message}
        </div>
      )}

      {/* Actions */}
      <div className="space-y-6">
        {/* Create Test Users */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">
            Create Test Users
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            Creates 30 test users with realistic names for testing purposes.
          </p>
          <button
            onClick={createTestUsers}
            disabled={loading}
            className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg transition-colors"
          >
            {loading ? 'Creating...' : 'Create 30 Test Users'}
          </button>
        </div>

        {/* Create Test Event */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">
            Create Test Event with Signups
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Event Title
              </label>
              <input
                type="text"
                value={eventForm.title}
                onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                placeholder="Test Blood on the Clocktower Game"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description
              </label>
              <input
                type="text"
                value={eventForm.description}
                onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                placeholder="A test game for development"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Days from Now
              </label>
              <input
                type="number"
                value={eventForm.daysFromNow}
                onChange={(e) => setEventForm({ ...eventForm, daysFromNow: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>
          <button
            onClick={createTestEvent}
            disabled={loading}
            className="bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg transition-colors"
          >
            {loading ? 'Creating...' : 'Create Test Event'}
          </button>
        </div>

        {/* Sign Up Users to Event */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">
            Sign Up Users to Event
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Event ID
              </label>
              <input
                type="text"
                value={signupForm.eventId}
                onChange={(e) => setSignupForm({ ...signupForm, eventId: e.target.value })}
                placeholder="Enter event ID"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Confirmed
              </label>
              <input
                type="number"
                value={signupForm.confirmed}
                onChange={(e) => setSignupForm({ ...signupForm, confirmed: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Tentative
              </label>
              <input
                type="number"
                value={signupForm.tentative}
                onChange={(e) => setSignupForm({ ...signupForm, tentative: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Late
              </label>
              <input
                type="number"
                value={signupForm.late}
                onChange={(e) => setSignupForm({ ...signupForm, late: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Declined
              </label>
              <input
                type="number"
                value={signupForm.declined}
                onChange={(e) => setSignupForm({ ...signupForm, declined: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>
          <button
            onClick={signupUsersToEvent}
            disabled={loading || !signupForm.eventId}
            className="bg-purple-500 hover:bg-purple-600 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg transition-colors"
          >
            {loading ? 'Signing Up...' : 'Sign Up Users'}
          </button>
        </div>

        {/* Clear Test Data */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">
            Clear Test Data
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            Removes all test users and their signups. This action cannot be undone.
          </p>
          <button
            onClick={clearTestData}
            disabled={loading}
            className="bg-red-500 hover:bg-red-600 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg transition-colors"
          >
            {loading ? 'Clearing...' : 'Clear All Test Data'}
          </button>
        </div>

        {/* Add Preferences to Existing Users */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">
            Add Preferences to Existing Users
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            Adds player and storyteller preferences to all existing test users.
          </p>
          <button
            onClick={addPreferencesToExistingUsers}
            disabled={loading}
            className="bg-orange-500 hover:bg-orange-600 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg transition-colors"
          >
            {loading ? 'Adding...' : 'Add Preferences to All Users'}
          </button>
        </div>

        {/* Regenerate Preferences */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">
            Regenerate Preferences
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            Clears all existing preferences and regenerates new ones for all test users.
          </p>
          <button
            onClick={regeneratePreferences}
            disabled={loading}
            className="bg-yellow-500 hover:bg-yellow-600 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg transition-colors"
          >
            {loading ? 'Regenerating...' : 'Regenerate All Preferences'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TestEnvironment;